import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ClipboardList, Loader2, Search, ShieldCheck, Timer } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import MovingBg, { BG } from "../MovingBg";

const CATEGORIES = ["Shipment Delay", "Pickup Issue", "Delivery Issue", "Tracking Issue",
  "Address Correction", "Billing", "International Query", "Account Issue", "Technical Issue", "Other"];

const ASSURANCES = [
  { icon: Timer, title: "First response within 24 hours", desc: "Our support desk reviews every complaint the same working day." },
  { icon: ShieldCheck, title: "Dedicated resolution desk", desc: "Delay, damage, billing or pickup — routed to the right team instantly." },
  { icon: Search, title: "Track every update", desc: "Admin replies appear on your ticket — check anytime with ticket ID + phone." },
];

export default function ComplaintSection() {
  const [form, setForm] = useState({ name: "", phone: "", awb: "", category: "Shipment Delay", description: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.awb.trim()) { toast.error("AWB / Tracking number is required"); return; }
    setBusy(true);
    try {
      const res = await api.post("/public/support-tickets", form);
      setDone(res.data.ticket_id);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const input = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-navy outline-none focus:border-saffron";

  return (
    <section id="complaints" className="tri-wash relative scroll-mt-20 overflow-hidden py-20" data-testid="home-complaint-section">
      <MovingBg src={BG.warehouse} anim="kenburns-b" opacity={0.10} />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-saffron">Complaints & Support</p>
          <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
            Something went wrong? Raise a complaint — track it to resolution
          </h2>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-600">
            Every complaint gets a unique ticket ID. Our team posts replies directly on your ticket,
            and you can follow every update using your ticket number and phone.
          </p>
          <div className="mt-8 space-y-5">
            {ASSURANCES.map((a, i) => (
              <div key={a.title} className="flex gap-4" data-testid={`complaint-assurance-${i}`}>
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-navy text-white">
                  <a.icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-heading text-sm font-bold text-navy">{a.title}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <Link to="/support" data-testid="complaint-track-link"
                className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-express hover:text-saffron">
            <Search className="h-4 w-4" /> Already have a ticket? Track its status
          </Link>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-xl shadow-slate-200/60">
          {done ? (
            <div className="py-8 text-center" data-testid="complaint-success">
              <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
              <h3 className="mt-4 font-heading text-xl font-bold text-navy">Complaint Registered</h3>
              <p className="mt-2 text-sm text-slate-500">Save this ticket ID to track replies from our team:</p>
              <p className="mt-3 font-mono text-2xl font-extrabold text-express" data-testid="complaint-ref-id">{done}</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link to="/support" data-testid="complaint-success-track-btn"
                      className="rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white hover:bg-express">
                  Track Ticket
                </Link>
                <button onClick={() => { setDone(null); setForm({ name: "", phone: "", awb: "", category: "Shipment Delay", description: "" }); }}
                        data-testid="complaint-new-btn"
                        className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-navy hover:bg-slate-100">
                  New Complaint
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="complaint-form">
              <h3 className="font-heading text-lg font-bold text-navy">Raise a Complaint</h3>
              <p className="mt-1 text-xs text-slate-500">No login needed — we verify you by phone when you track.</p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <input required className={input} data-testid="complaint-name" placeholder="Full name" value={form.name} onChange={set("name")} />
                <input required className={input} data-testid="complaint-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} />
                <input required className={input} data-testid="complaint-awb" placeholder="AWB / Tracking no. *" value={form.awb} onChange={set("awb")} />
                <select className={input} data-testid="complaint-category" value={form.category} onChange={set("category")}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <textarea required rows={4} className={`${input} sm:col-span-2`} data-testid="complaint-description"
                          placeholder="Describe your complaint — what happened, when, and what you expect…" value={form.description} onChange={set("description")} />
              </div>
              <button disabled={busy} data-testid="complaint-submit-btn"
                      className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-saffron py-3.5 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />} Submit Complaint
              </button>
              <p className="mt-3 text-center text-[11px] text-slate-400">Our team will also reach you on the phone number provided.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
